/**
 * Sanction Virtual Try-On — storefront widget.
 *
 * Entry point is snippets/product-tryon.liquid (data contract + hidden button).
 * No network calls happen at page load; everything activates on first click.
 *
 * Modules:
 *   CONFIG    — endpoints, storage keys, image prep constants
 *   Visitor   — stable visitor id (localStorage, validated, memory fallback)
 *   Store     — IndexedDB photo + wardrobe persistence (memory fallback)
 *   ImagePrep — client-side downscale/re-encode to JPEG (strips EXIF, HEIC)
 *   Api       — Product Tools backend (status / generate / event beacons)
 *   Modal     — singleton dialog: focus trap, scroll lock, toasts
 *   App       — state machine + rendering for every screen
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  var CONFIG = {
    apiUrl: 'https://product-tools.sanctionsnow.com',
    // apiUrl: 'http://localhost:3000', // Dev
    visitorKey: 'sanction_tryon_visitor',
    statusKey: 'sanction_tryon_status_v1', // sessionStorage: cached enabled flag (10 min)
    dbName: 'sanction-tryon',
    dbVersion: 1,
    maxEdge: 1536,
    jpegQuality: 0.85,
    maxFileBytes: 10 * 1024 * 1024,
    wardrobeCap: 20,
    generateTimeoutMs: 90000,
    visitorPattern: /^[A-Za-z0-9_-]{8,64}$/,
    // Mirrors snippets/product-tryon.liquid + DEFAULT_WEARABLE_TYPES (downcased).
    wearableTypes: [
      't shirt', 'sweatshirts', 'button up', 'womens apparel', 'mens thermal',
      'womens thermal', 'youth thermal', 'mens snowboard jackets',
      'womens snowboard jackets', 'casual jackets', 'youth jackets',
      'mens snowboard pants', 'womens snowboard pants', 'youth pants', 'pants',
      'shorts', 'beanie', 'hats', 'facemask', 'gloves & mitts', 'socks',
      'snowboard socks', 'belt', 'shoes', 'kids shoes', 'mens snowboard boots',
      'womens snowboard boots', 'kids snowboard boots', 'sunglasses', 'goggles',
      'snowboard helmet', 'skateboard helmet',
    ],
  };

  // ---------------------------------------------------------------------------
  // Small shared helpers
  // ---------------------------------------------------------------------------
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Shopify price fields arrive in two shapes: strings like "222.00" (dollars,
  // from the liquid money filter) or numbers in cents from the recommendations
  // JSON. Numeric input is always cents.
  function moneyNumber(value) {
    if (typeof value === 'string') {
      var n = parseFloat(value.replace(/[^0-9.]/g, ''));
      return isNaN(n) ? 0 : n;
    }
    if (typeof value === 'number' && isFinite(value)) {
      return value / 100;
    }
    return 0;
  }

  function formatMoney(dollars) {
    return '$' + (Math.round(dollars * 100) / 100).toFixed(2);
  }

  // Older wardrobe entries may lack a type; missing types normalize to '' and
  // are never used for exclusion matching.
  function normType(type) {
    return String(type || '').toLowerCase().trim();
  }

  // Gender/age lane from product text. Order matters: "womens" contains "men"
  // with no word boundary before it, so women is checked first, then youth,
  // then men. null = unisex or no signal.
  function laneOf(str) {
    var s = String(str || '').toLowerCase();
    if (/\bwomen'?s?\b|\bwmns\b/.test(s)) return 'women';
    if (/\byouth\b|\bkids?\b/.test(s)) return 'youth';
    if (/\bmen'?s?\b/.test(s)) return 'men';
    return null;
  }

  function productLane(p) {
    if (!p) return null;
    return laneOf((p.type || '') + ' ' + (p.title || ''));
  }

  function base64ToBlob(b64, mime) {
    try {
      var bin = atob(b64);
      var bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime || 'image/jpeg' });
    } catch (_) {
      return null;
    }
  }

  function track(name) {
    if (typeof window.plausible === 'function') {
      try {
        window.plausible(name, { props: { product: App.product ? App.product.title : '' } });
      } catch (_) { /* ignore */ }
    }
  }

  // The 'open' backend beacon fires once per page view; Plausible keeps
  // firing per open.
  var openBeaconSent = false;

  function reducedMotion() {
    try {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  }

  // Inline icons — 20px stroke glyphs, consistent with Lucide styling.
  var ICONS = {
    close: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    upload: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>',
    lock: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
    trash: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>',
    chevron: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
    camera: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>',
    expand: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>',
    plus: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    minus: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>',
    sparkle: '&#10024;',
  };

  // ---------------------------------------------------------------------------
  // Module: Visitor
  // ---------------------------------------------------------------------------
  var Visitor = {
    _id: null,

    id: function () {
      if (this._id) return this._id;
      var v = null;
      try { v = localStorage.getItem(CONFIG.visitorKey); } catch (_) { /* ignore */ }
      if (!v || !CONFIG.visitorPattern.test(v)) {
        // Seed from the chat widget's visitor id when present so analytics
        // can join try-on and chat activity for the same shopper.
        var chatId = null;
        try { chatId = localStorage.getItem('sanction_chat_visitor'); } catch (_) { /* ignore */ }
        v = chatId && CONFIG.visitorPattern.test(chatId) ? chatId : this.fresh();
        try { localStorage.setItem(CONFIG.visitorKey, v); } catch (_) { /* in-memory only */ }
      }
      this._id = v;
      return v;
    },

    fresh: function () {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        try { return window.crypto.randomUUID(); } catch (_) { /* fall through */ }
      }
      var chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      var out = 'v';
      for (var i = 0; i < 23; i++) out += chars[Math.floor(Math.random() * chars.length)];
      return out;
    },

    regenerate: function () {
      this._id = null;
      try { localStorage.removeItem(CONFIG.visitorKey); } catch (_) { /* ignore */ }
      return this.id();
    },
  };

  // ---------------------------------------------------------------------------
  // Module: Store (IndexedDB with in-memory fallback for private browsing)
  // ---------------------------------------------------------------------------
  var Store = {
    useMemory: false,
    memory: { photo: null, wardrobe: [], nextId: 1 },
    _dbPromise: null,

    open: function () {
      if (this.useMemory) return Promise.resolve(null);
      if (this._dbPromise) return this._dbPromise;
      var self = this;
      this._dbPromise = new Promise(function (resolve) {
        var req;
        try {
          req = window.indexedDB.open(CONFIG.dbName, CONFIG.dbVersion);
        } catch (_) {
          self.useMemory = true;
          resolve(null);
          return;
        }
        req.onupgradeneeded = function (e) {
          var db = e.target.result;
          if (!db.objectStoreNames.contains('photo')) {
            db.createObjectStore('photo');
          }
          if (!db.objectStoreNames.contains('wardrobe')) {
            db.createObjectStore('wardrobe', { keyPath: 'id', autoIncrement: true });
          }
        };
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { self.useMemory = true; resolve(null); };
        req.onblocked = function () { self.useMemory = true; resolve(null); };
      });
      return this._dbPromise;
    },

    getPhoto: function () {
      var self = this;
      return this.open().then(function (db) {
        if (!db) return self.memory.photo;
        return new Promise(function (resolve) {
          try {
            var rq = db.transaction('photo', 'readonly').objectStore('photo').get('me');
            rq.onsuccess = function () { resolve(rq.result ? rq.result.blob : null); };
            rq.onerror = function () { resolve(null); };
          } catch (_) { resolve(null); }
        });
      }).catch(function () { return self.memory.photo; });
    },

    setPhoto: function (blob) {
      var self = this;
      this.memory.photo = blob;
      return this.open().then(function (db) {
        if (!db) return;
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('photo', 'readwrite');
            tx.objectStore('photo').put({ blob: blob, updatedAt: Date.now() }, 'me');
            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { /* memory already has it */ });
    },

    deletePhoto: function () {
      this.memory.photo = null;
      return this.open().then(function (db) {
        if (!db) return;
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('photo', 'readwrite');
            tx.objectStore('photo').delete('me');
            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { /* memory already cleared */ });
    },

    clearWardrobe: function () {
      var self = this;
      this.memory.wardrobe = [];
      return this.open().then(function (db) {
        if (!db) return;
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('wardrobe', 'readwrite');
            tx.objectStore('wardrobe').clear();
            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { self.memory.wardrobe = []; });
    },

    listWardrobe: function () {
      var self = this;
      return this.open().then(function (db) {
        if (!db) return self.memory.wardrobe.slice().reverse();
        return new Promise(function (resolve) {
          try {
            var rq = db.transaction('wardrobe', 'readonly').objectStore('wardrobe').getAll();
            rq.onsuccess = function () {
              var list = rq.result || [];
              list.sort(function (a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
              resolve(list);
            };
            rq.onerror = function () { resolve([]); };
          } catch (_) { resolve([]); }
        });
      }).catch(function () { return []; });
    },

    countWardrobe: function () {
      var self = this;
      return this.open().then(function (db) {
        if (!db) return self.memory.wardrobe.length;
        return new Promise(function (resolve) {
          try {
            var rq = db.transaction('wardrobe', 'readonly').objectStore('wardrobe').count();
            rq.onsuccess = function () { resolve(rq.result || 0); };
            rq.onerror = function () { resolve(0); };
          } catch (_) { resolve(0); }
        });
      }).catch(function () { return 0; });
    },

    addLook: function (entry) {
      var self = this;
      return this.open().then(function (db) {
        if (!db) {
          entry.id = self.memory.nextId++;
          self.memory.wardrobe.push(entry);
          while (self.memory.wardrobe.length > CONFIG.wardrobeCap) self.memory.wardrobe.shift();
          return;
        }
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('wardrobe', 'readwrite');
            var store = tx.objectStore('wardrobe');
            store.add(entry);
            tx.oncomplete = function () { self._trim(db); resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { /* ignore */ });
    },

    // Upsert an entry with its existing id (undo restore, in-place update).
    putLook: function (entry) {
      var self = this;
      return this.open().then(function (db) {
        if (!db) {
          self.memory.wardrobe = self.memory.wardrobe.filter(function (e) { return e.id !== entry.id; });
          self.memory.wardrobe.push(entry);
          self.memory.wardrobe.sort(function (a, b) { return (a.id || 0) - (b.id || 0); });
          return;
        }
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('wardrobe', 'readwrite');
            tx.objectStore('wardrobe').put(entry);
            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { /* ignore */ });
    },

    // Update a saved look in place (same id): used when layering onto a
    // saved look from the look detail view.
    updateLook: function (entry) {
      return this.putLook(entry);
    },

    deleteLook: function (id) {
      var self = this;
      return this.open().then(function (db) {
        if (!db) {
          self.memory.wardrobe = self.memory.wardrobe.filter(function (e) { return e.id !== id; });
          return;
        }
        return new Promise(function (resolve) {
          try {
            var tx = db.transaction('wardrobe', 'readwrite');
            tx.objectStore('wardrobe').delete(id);
            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () { resolve(); };
          } catch (_) { resolve(); }
        });
      }).catch(function () { /* ignore */ });
    },

    // Enforce the wardrobe cap by deleting oldest entries beyond it.
    _trim: function (db) {
      try {
        var store = db.transaction('wardrobe', 'readwrite').objectStore('wardrobe');
        var countReq = store.count();
        countReq.onsuccess = function () {
          var extra = (countReq.result || 0) - CONFIG.wardrobeCap;
          if (extra <= 0) return;
          var cursorReq = store.openCursor(); // key order = insertion order
          cursorReq.onsuccess = function () {
            var cursor = cursorReq.result;
            if (cursor && extra > 0) {
              cursor.delete();
              extra--;
              cursor.continue();
            }
          };
        };
      } catch (_) { /* ignore */ }
    },
  };

  // ---------------------------------------------------------------------------
  // Module: ImagePrep — downscale + re-encode to JPEG on the client.
  // Converts HEIC where the browser can decode it, strips EXIF, keeps uploads
  // small. If decoding fails we send the original file; the server re-validates.
  // ---------------------------------------------------------------------------
  var ImagePrep = {
    fileToJpeg: function (file) {
      if (file.size > CONFIG.maxFileBytes) {
        return Promise.reject({ code: 'file_too_large' });
      }
      var self = this;
      return this._decode(file).then(function (source) {
        if (!source) return file;
        var w = source.naturalWidth || source.width;
        var h = source.naturalHeight || source.height;
        if (!w || !h) { self._release(source); return file; }
        var scale = Math.min(1, CONFIG.maxEdge / Math.max(w, h));
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        var ctx = canvas.getContext('2d');
        if (!ctx) { self._release(source); return file; }
        try {
          ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
        } catch (_) {
          self._release(source);
          return file;
        }
        self._release(source);
        return new Promise(function (resolve) {
          try {
            canvas.toBlob(function (blob) { resolve(blob || file); }, 'image/jpeg', CONFIG.jpegQuality);
          } catch (_) { resolve(file); }
        });
      }).then(function (blob) {
        if (blob.size > CONFIG.maxFileBytes) throw { code: 'file_too_large' };
        return blob;
      });
    },

    _decode: function (file) {
      var self = this;
      if (typeof window.createImageBitmap === 'function') {
        try {
          return window.createImageBitmap(file).catch(function () { return self._decodeImg(file); });
        } catch (_) { /* fall through */ }
      }
      return this._decodeImg(file);
    },

    _decodeImg: function (file) {
      return new Promise(function (resolve) {
        var url;
        try { url = URL.createObjectURL(file); } catch (_) { resolve(null); return; }
        var img = new Image();
        img.onload = function () { img.__objectUrl = url; resolve(img); };
        img.onerror = function () {
          try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
          resolve(null);
        };
        img.src = url;
      });
    },

    _release: function (source) {
      if (!source) return;
      if (typeof source.close === 'function') {
        try { source.close(); } catch (_) { /* ignore */ }
      }
      if (source.__objectUrl) {
        try { URL.revokeObjectURL(source.__objectUrl); } catch (_) { /* ignore */ }
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Module: Camera — desktop/laptop capture. Mobile file inputs already offer
  // the camera natively; this is the laptop path. Tracks are stopped the
  // moment they are no longer needed so the camera light never lingers.
  // ---------------------------------------------------------------------------
  var Camera = {
    stream: null,
    countTimer: null,

    supported: function () {
      return !!(window.isSecureContext && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    },

    start: function () {
      var self = this;
      this.stop();
      return navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1920 } },
      }).then(function (stream) {
        self.stream = stream;
        return stream;
      });
    },

    stop: function () {
      if (this.countTimer) {
        clearInterval(this.countTimer);
        this.countTimer = null;
      }
      if (this.stream) {
        try {
          this.stream.getTracks().forEach(function (t) {
            try { t.stop(); } catch (_) { /* ignore */ }
          });
        } catch (_) { /* ignore */ }
        this.stream = null;
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Module: Api
  // ---------------------------------------------------------------------------
  var Api = {
    abortController: null,

    status: function () {
      var url = CONFIG.apiUrl + '/api/tryon/status?visitorId=' + encodeURIComponent(Visitor.id());
      return fetch(url).then(function (res) {
        if (res.status !== 200) return null;
        return res.json().then(function (data) {
          return data && typeof data === 'object' ? data : null;
        }, function () { return null; });
      }, function () { return null; });
    },

    /**
     * @returns {Promise<object>} { ok: true, image, remaining }
     *   or { ok: false, error, code, retryAfter, status, aborted }
     */
    generate: function (opts) {
      var fd = new FormData();
      fd.append('photo', opts.photoBlob, 'photo.jpg');
      fd.append('productHandle', opts.productHandle);
      fd.append('visitorId', Visitor.id());
      fd.append('mode', opts.mode || 'new');

      var controller = null;
      try { controller = new AbortController(); } catch (_) { /* very old browser */ }
      Api.abortController = controller;
      var timedOut = false;
      var timer = controller
        ? setTimeout(function () { timedOut = true; controller.abort(); }, CONFIG.generateTimeoutMs)
        : null;

      var init = { method: 'POST', body: fd };
      if (controller) init.signal = controller.signal;

      var settle = function (result) {
        if (timer) clearTimeout(timer);
        if (Api.abortController === controller) Api.abortController = null;
        return result;
      };

      return fetch(CONFIG.apiUrl + '/api/tryon/generate', init)
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            if (res.ok && data && data.image && data.image.base64) {
              return { ok: true, image: data.image, remaining: data.remaining };
            }
            return {
              ok: false,
              status: res.status,
              error: (data && data.error) || 'generation_failed',
              code: data && data.code,
              retryAfter: data && data.retryAfter,
            };
          });
        })
        .catch(function (err) {
          var aborted = !!(err && err.name === 'AbortError') && !timedOut;
          return { ok: false, error: aborted ? 'aborted' : 'network', aborted: aborted };
        })
        .then(settle);
    },

    cancel: function () {
      if (this.abortController) {
        try { this.abortController.abort(); } catch (_) { /* ignore */ }
        this.abortController = null;
      }
    },

    event: function (payload) {
      var url = CONFIG.apiUrl + '/api/tryon/event';
      var body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        try {
          var blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon(url, blob)) return;
        } catch (_) { /* fall through */ }
      }
      try {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
        }).catch(function () { /* swallow */ });
      } catch (_) { /* swallow */ }
    },

    cartToken: function () {
      return fetch('/cart.js').then(function (res) {
        if (!res.ok) return null;
        return res.json().then(function (cart) {
          // /cart.js returns "<token>?key=<hex>"; webhooks send the bare token.
          return cart && cart.token ? String(cart.token).split('?')[0] : null;
        }, function () { return null; });
      }, function () { return null; });
    },

    recommendations: function (productId, intent) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = root + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=8' +
        (intent ? '&intent=' + encodeURIComponent(intent) : '');
      return fetch(url).then(function (res) {
        if (!res.ok) return [];
        return res.json().then(function (data) {
          return (data && data.products) || [];
        }, function () { return []; });
      }, function () { return []; });
    },
  };

  // ---------------------------------------------------------------------------
  // Module: Modal — singleton dialog shell
  // ---------------------------------------------------------------------------
  var Modal = {
    root: null,
    dialog: null,
    body: null,
    closeBtn: null,
    toastEl: null,
    isOpen: false,
    lastFocused: null,
    scrollY: 0,
    _keyHandler: null,
    _toastTimer: null,

    build: function () {
      if (this.root) return;
      var root = document.createElement('div');
      root.className = 'product-tryon-overlay';
      root.hidden = true;
      root.innerHTML =
        '<div class="product-tryon-modal" role="dialog" aria-modal="true" aria-label="Virtual try-on">' +
          '<header class="product-tryon-modal__head">' +
            '<span class="product-tryon-modal__eyebrow">Fitting room</span>' +
            '<button type="button" class="product-tryon-modal__close" aria-label="Close try-on">' + ICONS.close + '</button>' +
          '</header>' +
          '<div class="product-tryon-modal__body"></div>' +
          '<div class="product-tryon-toast" role="status" hidden></div>' +
        '</div>';
      document.body.appendChild(root);
      this.root = root;
      this.dialog = root.querySelector('.product-tryon-modal');
      this.body = root.querySelector('.product-tryon-modal__body');
      this.closeBtn = root.querySelector('.product-tryon-modal__close');
      this.toastEl = root.querySelector('.product-tryon-toast');

      var self = this;
      this.closeBtn.addEventListener('click', function () { self.close(); });
      root.addEventListener('click', function (e) {
        if (e.target === root) self.close();
      });
      window.addEventListener('resize', function () {
        if (self.isOpen) self.syncOverflow();
      });
      this._keyHandler = function (e) { self._onKeydown(e); };
    },

    open: function () {
      this.build();
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocused = document.activeElement;
      // Guard against reopening during the close transition: the body is still
      // position: fixed then, so window.scrollY would read 0 and clobber the
      // saved position.
      if (!document.documentElement.classList.contains('product-tryon-lock')) {
        this.scrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = -this.scrollY + 'px';
        document.documentElement.classList.add('product-tryon-lock');
      }
      this.root.hidden = false;
      // Force a frame so the entrance transition runs from the initial state.
      void this.root.offsetWidth;
      this.root.classList.add('is-open');
      document.addEventListener('keydown', this._keyHandler, true);
      try { this.closeBtn.focus(); } catch (_) { /* ignore */ }
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.removeEventListener('keydown', this._keyHandler, true);
      Api.cancel();
      App.onClose();
      this.hideToast();
      var self = this;
      this.root.classList.remove('is-open');
      var finish = function () {
        if (self.isOpen) return; // reopened during the out transition
        self.root.hidden = true;
        document.documentElement.classList.remove('product-tryon-lock');
        document.body.style.top = '';
        window.scrollTo(0, self.scrollY);
        if (self.lastFocused && typeof self.lastFocused.focus === 'function') {
          try { self.lastFocused.focus(); } catch (_) { /* ignore */ }
        }
      };
      if (reducedMotion()) finish();
      else setTimeout(finish, 230);
    },

    _onKeydown: function (e) {
      if (!this.isOpen) return;
      if (Viewer.isOpen) return; // the lightbox owns the keyboard while open
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      var nodes = this.dialog.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]):not([hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.prototype.filter.call(nodes, function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
      if (list.length === 0) { e.preventDefault(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      if (list.length === 1) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (!this.dialog.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    toast: function (message, actionLabel, onAction) {
      if (!this.toastEl) return;
      var el = this.toastEl;
      clearTimeout(this._toastTimer);
      el.textContent = message;
      if (actionLabel) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-tryon-toast__action';
        btn.textContent = actionLabel;
        btn.addEventListener('click', function () {
          el.hidden = true;
          onAction();
        });
        el.appendChild(btn);
      }
      el.hidden = false;
      var self = this;
      this._toastTimer = setTimeout(function () { self.hideToast(); }, 5000);
    },

    hideToast: function () {
      clearTimeout(this._toastTimer);
      if (this.toastEl) this.toastEl.hidden = true;
    },

    // The sticky actions' fade should only appear when there is actually
    // content to scroll under it; over a static layout it washes out the
    // outfit title.
    syncOverflow: function () {
      if (!this.body) return;
      var overflowing = this.body.scrollHeight > this.body.clientHeight + 1;
      this.body.classList.toggle('is-overflowing', overflowing);
    },
  };

  // ---------------------------------------------------------------------------
  // Module: Viewer — fullscreen zoomable lightbox for the generated image.
  // One transform (translate + scale), zoom-to-point math, pinch via two
  // pointer distances, clamped 1x to 4x. Reuses the render's tracked blob
  // URL; App.render()/onClose close it before any URL is revoked.
  // ---------------------------------------------------------------------------
  var Viewer = {
    root: null,
    img: null,
    closeBtn: null,
    zoomOutBtn: null,
    zoomInBtn: null,
    isOpen: false,
    opener: null,
    scale: 1,
    tx: 0,
    ty: 0,
    baseW: 0,
    baseH: 0,
    pointers: {},
    pinch: null,
    panLast: null,
    lastTap: 0,
    lastTapX: 0,
    lastTapY: 0,
    _keyHandler: null,

    build: function () {
      if (this.root) return;
      var root = document.createElement('div');
      root.className = 'product-tryon-viewer';
      root.hidden = true;
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Try-on image viewer');
      root.innerHTML =
        '<img class="product-tryon-viewer__img" alt="">' +
        '<button type="button" class="product-tryon-viewer__close" aria-label="Close viewer">' + ICONS.close + '</button>' +
        '<div class="product-tryon-viewer__controls">' +
          '<button type="button" class="product-tryon-viewer__zoombtn" data-zoom="-1" aria-label="Zoom out">' + ICONS.minus + '</button>' +
          '<button type="button" class="product-tryon-viewer__zoombtn" data-zoom="1" aria-label="Zoom in">' + ICONS.plus + '</button>' +
        '</div>';
      document.body.appendChild(root);
      this.root = root;
      this.img = root.querySelector('.product-tryon-viewer__img');
      this.closeBtn = root.querySelector('.product-tryon-viewer__close');
      this.zoomOutBtn = root.querySelector('[data-zoom="-1"]');
      this.zoomInBtn = root.querySelector('[data-zoom="1"]');

      var self = this;
      this.closeBtn.addEventListener('click', function () { self.close(); });
      this.zoomOutBtn.addEventListener('click', function () { self.stepZoom(-1); });
      this.zoomInBtn.addEventListener('click', function () { self.stepZoom(1); });
      root.addEventListener('click', function (e) {
        // A click that just toggled zoom in pointerup must not also close.
        if (self._suppressClick) { self._suppressClick = false; return; }
        if (e.target === root && self.scale <= 1.01) self.close();
      });
      // Mouse model: single click does what the cursor promises (zoom-in at
      // the point, zoom-out when zoomed). Touch keeps double-tap (below);
      // a dblclick handler would fight the single-click toggle.
      root.addEventListener('wheel', function (e) {
        e.preventDefault();
        // ctrlKey marks trackpad pinch; give it a stronger response.
        var factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002));
        self.zoomAt(e.clientX, e.clientY, self.scale * factor);
      }, { passive: false });
      root.addEventListener('pointerdown', function (e) { self.onPointerDown(e); });
      root.addEventListener('pointermove', function (e) { self.onPointerMove(e); });
      root.addEventListener('pointerup', function (e) { self.onPointerUp(e); });
      root.addEventListener('pointercancel', function (e) { self.onPointerUp(e); });
      this._keyHandler = function (e) { self.onKeydown(e); };
    },

    open: function (url, alt, opener) {
      this.build();
      this.opener = opener || null;
      this.isOpen = true;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = {};
      this.pinch = null;
      this.panLast = null;
      this.img.src = url;
      this.img.alt = alt || '';
      this.root.hidden = false;
      this.apply();
      var self = this;
      requestAnimationFrame(function () {
        if (!self.isOpen) return;
        var r = self.img.getBoundingClientRect();
        self.baseW = r.width / self.scale;
        self.baseH = r.height / self.scale;
      });
      document.addEventListener('keydown', this._keyHandler, true);
      try { this.closeBtn.focus(); } catch (_) { /* ignore */ }
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.removeEventListener('keydown', this._keyHandler, true);
      this.root.hidden = true;
      try { this.img.removeAttribute('src'); } catch (_) { /* ignore */ }
      this.pointers = {};
      this.pinch = null;
      this.panLast = null;
      var target = this.opener;
      this.opener = null;
      if (target && document.contains(target)) {
        try { target.focus(); } catch (_) { /* ignore */ }
      } else if (Modal.isOpen && Modal.closeBtn) {
        try { Modal.closeBtn.focus(); } catch (_) { /* ignore */ }
      }
    },

    onKeydown: function (e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        this.stepZoom(1);
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        this.stepZoom(-1);
        return;
      }
      if (e.key === 'Tab') {
        // Cycle the viewer's three controls, skipping disabled ends.
        e.preventDefault();
        e.stopPropagation();
        var order = [this.closeBtn, this.zoomOutBtn, this.zoomInBtn].filter(function (b) {
          return b && !b.disabled;
        });
        if (!order.length) return;
        var idx = order.indexOf(document.activeElement);
        var next = idx === -1
          ? 0
          : (idx + (e.shiftKey ? -1 : 1) + order.length) % order.length;
        try { order[next].focus(); } catch (_) { /* ignore */ }
      }
    },

    // Explicit steps for people who never discover wheel or double-tap.
    stepZoom: function (dir) {
      var self = this;
      if (!reducedMotion()) {
        this.img.classList.add('is-anim');
        setTimeout(function () { self.img.classList.remove('is-anim'); }, 240);
      }
      this.zoomAt(window.innerWidth / 2, window.innerHeight / 2, this.scale + dir * 0.5);
    },

    zoomAt: function (x, y, newScale) {
      newScale = Math.max(1, Math.min(4, newScale));
      var dx = x - window.innerWidth / 2;
      var dy = y - window.innerHeight / 2;
      var k = newScale / this.scale;
      this.tx = dx - (dx - this.tx) * k;
      this.ty = dy - (dy - this.ty) * k;
      this.scale = newScale;
      this.clampPan();
      this.apply();
    },

    toggleZoom: function (x, y) {
      var self = this;
      if (!reducedMotion()) {
        this.img.classList.add('is-anim');
        setTimeout(function () { self.img.classList.remove('is-anim'); }, 240);
      }
      if (this.scale > 1.05) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.apply();
      } else {
        this.zoomAt(x, y, 2.5);
      }
    },

    clampPan: function () {
      if (this.scale <= 1.001) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        return;
      }
      var maxX = Math.max(0, (this.baseW * this.scale - window.innerWidth) / 2 + 40);
      var maxY = Math.max(0, (this.baseH * this.scale - window.innerHeight) / 2 + 40);
      this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
      this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
    },

    apply: function () {
      this.img.style.transform =
        'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
      this.root.classList.toggle('is-zoomed', this.scale > 1.01);
      if (this.zoomOutBtn) this.zoomOutBtn.disabled = this.scale <= 1.001;
      if (this.zoomInBtn) this.zoomInBtn.disabled = this.scale >= 3.999;
    },

    onPointerDown: function (e) {
      // Buttons handle their own clicks; keep them out of pan/pinch/tap logic.
      if (e.target && e.target.closest && e.target.closest('button')) return;
      this.pointers[e.pointerId] = {
        x: e.clientX, y: e.clientY,
        startX: e.clientX, startY: e.clientY,
        t: Date.now(), type: e.pointerType,
        // Captured pointerup retargets to root, so remember where it began.
        onImg: e.target === this.img,
      };
      try { this.root.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
      var ids = Object.keys(this.pointers);
      if (ids.length === 2) {
        var a = this.pointers[ids[0]];
        var b = this.pointers[ids[1]];
        this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
        this.panLast = null;
      } else if (ids.length === 1 && this.scale > 1.01) {
        this.panLast = { x: e.clientX, y: e.clientY };
      }
    },

    onPointerMove: function (e) {
      var p = this.pointers[e.pointerId];
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;
      var ids = Object.keys(this.pointers);
      if (ids.length >= 2 && this.pinch && this.pinch.dist > 0) {
        var a = this.pointers[ids[0]];
        var b = this.pointers[ids[1]];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        this.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, this.pinch.scale * (dist / this.pinch.dist));
      } else if (ids.length === 1 && this.panLast && this.scale > 1.01) {
        this.tx += e.clientX - this.panLast.x;
        this.ty += e.clientY - this.panLast.y;
        this.panLast = { x: e.clientX, y: e.clientY };
        this.clampPan();
        this.apply();
      }
    },

    onPointerUp: function (e) {
      var p = this.pointers[e.pointerId];
      if (!p) return;
      delete this.pointers[e.pointerId];
      var ids = Object.keys(this.pointers);
      if (ids.length < 2) this.pinch = null;
      if (ids.length === 0) {
        this.panLast = null;
      } else if (ids.length === 1 && this.scale > 1.01) {
        var rem = this.pointers[ids[0]];
        this.panLast = { x: rem.x, y: rem.y };
      }
      // Mouse/pen: a clean click on the image toggles zoom at that point,
      // matching the zoom-in / zoom-out cursor. Drags (pans) don't count.
      if (p.type !== 'touch' && p.onImg) {
        var mouseMoved = Math.hypot(e.clientX - p.startX, e.clientY - p.startY);
        if (mouseMoved < 4) {
          this._suppressClick = true;
          this.toggleZoom(e.clientX, e.clientY);
        }
      }
      // Touch double-tap: dblclick is unreliable on iOS, so detect it here.
      if (p.type === 'touch') {
        var moved = Math.hypot(e.clientX - p.startX, e.clientY - p.startY);
        if (moved < 10 && Date.now() - p.t < 350) {
          var now = Date.now();
          var near = Math.hypot(e.clientX - this.lastTapX, e.clientY - this.lastTapY) < 60;
          if (now - this.lastTap < 320 && near) {
            this.lastTap = 0;
            this.toggleZoom(e.clientX, e.clientY);
          } else {
            this.lastTap = now;
            this.lastTapX = e.clientX;
            this.lastTapY = e.clientY;
          }
        }
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Module: App — state machine + rendering
  // States: upload | ready | generating | result | wardrobe | limit | unavailable
  // ---------------------------------------------------------------------------
  var App = {
    container: null,
    triggers: [],
    product: null,
    state: null,
    returnState: 'ready',   // where wardrobe's back link goes
    photoBlob: null,
    remaining: null,
    dailyLimit: null,
    outfit: [],
    resultBlob: null,
    resultMime: 'image/jpeg',
    recQueue: [],
    recSourceId: null,
    recPromise: null,
    pendingRec: null,
    layerTarget: null,   // wardrobe entry being layered onto (look view)
    currentLook: null,   // wardrobe entry open in the look detail view
    _genBackdrop: null,
    generating: false,
    atcBusy: false,
    progress: 0,
    progressTimer: null,
    inlineError: null,      // { message, retry } shown on upload/ready
    layerError: false,      // inline message on result/look, or false
    wardrobeCount: 0,
    _urls: [],

    // -- lifecycle ------------------------------------------------------------

    init: function () {
      var container = document.querySelector('.product-tryon-container');
      if (!container) return;
      this.container = container;
      var d = container.dataset;
      this.product = {
        handle: d.productHandle || '',
        id: d.productId || '',
        title: d.productTitle || '',
        type: d.productType || '',
        priceText: d.productPrice || '',
        image: d.productImage || '',
        variantId: d.variantId || '',
      };
      // (The reveal gate below owns the enabled/disabled decision; a legacy
      // permanent session flag used to early-return here and made re-enables
      // invisible until the tab closed.)
      // The floating pill on the media gallery is the sole entry point; the
      // container above renders nothing visible and only carries the product
      // dataset. The admin kill switch must actually hide the pill, so reveal
      // is gated on a status check, cached in sessionStorage for 10 minutes
      // so repeat page views inside a session stay network-free.
      var self = this;
      this.triggers = [];
      var pill = document.querySelector('.product-tryon-pill');
      if (!pill) return;
      pill.addEventListener('click', function () { self.openModal(); });
      this.triggers.push(pill);
      this.checkEnabled(function (enabled) {
        if (enabled) pill.hidden = false;
      });
    },

    // Cached feature-flag check. Fresh cache -> no request. On network
    // failure the pill stays hidden (a dead backend would only offer a
    // dead-end modal) but nothing is cached, so the next view retries.
    checkEnabled: function (cb) {
      var cached = null;
      try { cached = JSON.parse(sessionStorage.getItem(CONFIG.statusKey)); } catch (_) { /* ignore */ }
      if (cached && typeof cached.enabled === 'boolean' && cached.exp > Date.now()) {
        cb(cached.enabled);
        return;
      }
      Api.status().then(function (status) {
        var enabled = !!(status && status.enabled);
        if (status) {
          try {
            sessionStorage.setItem(CONFIG.statusKey, JSON.stringify({
              enabled: enabled,
              exp: Date.now() + 10 * 60 * 1000,
            }));
          } catch (_) { /* ignore */ }
        }
        cb(enabled);
      }, function () { cb(false); });
    },

    openModal: function () {
      Modal.open();
      this.resetSession();
      this.renderLoading();

      track('TryOn Opened');
      if (!openBeaconSent) {
        openBeaconSent = true;
        // productId carries the HANDLE: the backend keys generate/layer events
        // by handle, and mixing in the numeric id would split admin stats.
        Api.event({
          eventType: 'open',
          productId: this.product.handle,
          productTitle: this.product.title,
          variantId: this.liveVariantId(),
          cartToken: null,
          visitorId: Visitor.id(),
        });
      }

      var self = this;
      // A hung backend must not strand the shopper on the opening screen:
      // 5s without a status response routes to the unavailable state.
      var statusP = Promise.race([
        Api.status(),
        new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 5000); }),
      ]);
      Promise.all([statusP, Store.getPhoto(), Store.countWardrobe()]).then(function (res) {
        if (!Modal.isOpen) return;
        var status = res[0];
        self.photoBlob = res[1];
        self.wardrobeCount = res[2] || 0;
        if (!status || status.enabled === false) {
          if (status && status.enabled === false) self.markDisabled();
          self.setState('unavailable');
          return;
        }
        self.remaining = typeof status.remaining === 'number' ? status.remaining : null;
        self.dailyLimit = typeof status.dailyLimit === 'number' ? status.dailyLimit : null;
        if (self.remaining === 0) {
          self.setState('limit');
          return;
        }
        self.setState(self.photoBlob ? 'ready' : 'upload');
      });
    },

    resetSession: function () {
      this.outfit = [this.primaryItem()];
      this.resultBlob = null;
      this.pendingRec = null;
      this.layerTarget = null;
      this.currentLook = null;
      this._genBackdrop = null;
      this.inlineError = null;
      this.layerError = false;
      this.generating = false;
      this.atcBusy = false;
      this.returnState = 'ready';
      this.endProgress();
    },

    onClose: function () {
      this.generating = false;
      this.endProgress();
      Viewer.close();
      this.revokeUrls();
      Camera.stop();
    },

    markDisabled: function () {
      // Same 10-minute cache the reveal gate reads, so a re-enable in the
      // dashboard propagates to every session within the window (a permanent
      // session flag here once kept the pill hidden until the tab closed).
      try {
        sessionStorage.setItem(CONFIG.statusKey, JSON.stringify({
          enabled: false,
          exp: Date.now() + 10 * 60 * 1000,
        }));
      } catch (_) { /* ignore */ }
      (this.triggers || []).forEach(function (el) { el.hidden = true; });
    },

    primaryItem: function () {
      return {
        id: String(this.product.id),
        title: this.product.title,
        price: moneyNumber(this.product.priceText),
        variantId: String(this.product.variantId),
        handle: this.product.handle,
        image: this.product.image,
        type: normType(this.product.type),
      };
    },

    liveVariantId: function () {
      var form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        var field = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
        if (field && field.value) return String(field.value);
      }
      return String(this.product.variantId);
    },

    // -- state + render dispatch ---------------------------------------------

    setState: function (name) {
      this.state = name;
      this.render();
    },

    render: function () {
      if (!Modal.body) return;
      Viewer.close(); // its image URL is about to be revoked
      this.revokeUrls();
      Modal.hideToast();
      switch (this.state) {
        case 'upload': this.renderUpload(); break;
        case 'ready': this.renderReady(); break;
        case 'generating': this.renderGenerating(); break;
        case 'result': this.renderResult(); break;
        case 'wardrobe': this.renderWardrobe(); break;
        case 'look': this.renderLook(); break;
        case 'camera': this.renderCamera(); break;
        case 'limit': this.renderLimit(); break;
        case 'unavailable': this.renderUnavailable(); break;
        default: this.renderLoading();
      }
      // Image-led screens pin the dialog tall so the image is the hero;
      // everything else sits at natural content height.
      var tall = this.state === 'result' || this.state === 'look' ||
        this.state === 'generating' || this.state === 'camera';
      if (Modal.dialog) Modal.dialog.classList.toggle('product-tryon-modal--tall', tall);
      Modal.syncOverflow();
      // Re-check once images have decoded and the height transition settled.
      setTimeout(function () { Modal.syncOverflow(); }, 350);
    },

    url: function (blob) {
      try {
        var u = URL.createObjectURL(blob);
        this._urls.push(u);
        return u;
      } catch (_) {
        return '';
      }
    },

    revokeUrls: function () {
      for (var i = 0; i < this._urls.length; i++) {
        try { URL.revokeObjectURL(this._urls[i]); } catch (_) { /* ignore */ }
      }
      this._urls = [];
    },

    // -- shared fragments -----------------------------------------------------

    errorCardHtml: function () {
      if (!this.inlineError) return '';
      return (
        '<div class="product-tryon-error" role="alert">' +
          '<p>' + escapeHtml(this.inlineError.message) + '</p>' +
          (this.inlineError.retry
            ? '<button type="button" class="product-tryon-error__retry" data-action="retry">Try again</button>'
            : '') +
        '</div>'
      );
    },

    creditsHtml: function () {
      if (typeof this.remaining !== 'number' || this.remaining > 2) return '';
      var word = this.remaining === 1 ? 'try-on' : 'try-ons';
      return '<p class="product-tryon-credits' + (this.remaining === 1 ? ' is-last' : '') + '">' +
        this.remaining + ' ' + word + ' left today</p>';
    },

    wardrobeRowHtml: function () {
      if (!this.wardrobeCount) return '';
      return (
        '<button type="button" class="product-tryon-wrow" data-action="wardrobe">' +
          '<span>My try-ons <span class="product-tryon-wrow__count">(' + this.wardrobeCount + ')</span></span>' +
          ICONS.chevron +
        '</button>'
      );
    },

    wireCommon: function () {
      var self = this;
      var wrow = Modal.body.querySelector('[data-action="wardrobe"]');
      if (wrow) {
        wrow.addEventListener('click', function () {
          self.returnState = self.state;
          self.setState('wardrobe');
        });
      }
      var retry = Modal.body.querySelector('[data-action="retry"]');
      if (retry) {
        retry.addEventListener('click', function () {
          self.inlineError = null;
          self.startGenerate('new');
        });
      }
    },

    // -- screens --------------------------------------------------------------

    renderLoading: function () {
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill product-tryon-loading">' +
          '<div class="product-tryon-spinner" aria-hidden="true"></div>' +
          '<p class="product-tryon-loading__text">Opening the fitting room</p>' +
        '</div>';
    },

    renderUpload: function () {
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill product-tryon-screen--vcenter">' +
          '<h2 class="product-tryon-headline">Virtual try-on</h2>' +
          '<p class="product-tryon-sub">See it on you before you buy.</p>' +
          '<button type="button" class="product-tryon-drop">' +
            '<span class="product-tryon-drop__icon">' + ICONS.upload + '</span>' +
            '<span class="product-tryon-drop__label">Upload a full-body photo</span>' +
            '<span class="product-tryon-drop__hint">JPG, PNG or HEIC, up to 10MB</span>' +
          '</button>' +
          '<input type="file" class="product-tryon-file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*" hidden>' +
          (Camera.supported()
            ? '<button type="button" class="product-tryon-camlink" data-action="camera">' + ICONS.camera + ' Or use your camera</button>'
            : '') +
          '<p class="product-tryon-tip">Stand facing the camera in good lighting.</p>' +
          this.errorCardHtml() +
          '<p class="product-tryon-privacy">' + ICONS.lock + ' Your photo is processed once and never stored on our servers. It stays on this device until you remove it.</p>' +
          this.wardrobeRowHtml() +
        '</div>';

      var self = this;
      var drop = Modal.body.querySelector('.product-tryon-drop');
      var input = Modal.body.querySelector('.product-tryon-file');

      drop.addEventListener('click', function () { input.click(); });
      input.addEventListener('change', function () {
        if (input.files && input.files[0]) self.handleFile(input.files[0], drop);
      });
      drop.addEventListener('dragover', function (e) {
        e.preventDefault();
        drop.classList.add('is-drag');
      });
      drop.addEventListener('dragleave', function () {
        drop.classList.remove('is-drag');
      });
      drop.addEventListener('drop', function (e) {
        e.preventDefault();
        drop.classList.remove('is-drag');
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) self.handleFile(file, drop);
      });
      var camLink = Modal.body.querySelector('[data-action="camera"]');
      if (camLink) {
        camLink.addEventListener('click', function () {
          self.inlineError = null;
          self.setState('camera');
        });
      }
      this.wireCommon();
    },

    handleFile: function (file, dropEl) {
      if (this.generating) return;
      if (file.type && file.type.indexOf('image/') !== 0) {
        this.inlineError = { message: 'That file is not a photo. Choose a JPG, PNG or HEIC image.' };
        this.render();
        return;
      }
      var self = this;
      this.inlineError = null;
      if (dropEl) {
        dropEl.disabled = true;
        var label = dropEl.querySelector('.product-tryon-drop__label');
        if (label) label.textContent = 'Preparing photo';
      }
      ImagePrep.fileToJpeg(file).then(function (blob) {
        self.photoBlob = blob;
        return Store.setPhoto(blob);
      }).then(function () {
        if (!Modal.isOpen) return;
        self.setState('ready');
      }).catch(function (err) {
        if (!Modal.isOpen) return;
        self.inlineError = {
          message: err && err.code === 'file_too_large'
            ? 'That photo is over 10MB. Choose a smaller one.'
            : 'We could not read that photo. Try a different one.',
        };
        self.render();
      });
    },

    renderReady: function () {
      var photoUrl = this.photoBlob ? this.url(this.photoBlob) : '';
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill product-tryon-screen--vcenter">' +
          '<div class="product-tryon-pair">' +
            '<figure class="product-tryon-card product-tryon-card--you">' +
              (photoUrl
                ? '<img class="product-tryon-card__img product-tryon-card__img--cover" src="' + photoUrl + '" alt="Your photo">'
                : '') +
              '<button type="button" class="product-tryon-card__change" data-action="change">Change photo</button>' +
            '</figure>' +
            '<span class="product-tryon-plus" aria-hidden="true">+</span>' +
            '<figure class="product-tryon-card">' +
              '<img class="product-tryon-card__img" src="' + escapeHtml(this.product.image) + '" alt="">' +
              '<figcaption class="product-tryon-card__caption">' + escapeHtml(this.product.title) + '</figcaption>' +
            '</figure>' +
          '</div>' +
          '<button type="button" class="product-tryon-quietlink" data-action="removephoto">Remove photo</button>' +
          this.errorCardHtml() +
          '<button type="button" class="btn product-tryon-cta" data-action="generate">Try it on</button>' +
          this.creditsHtml() +
          this.wardrobeRowHtml() +
        '</div>';

      var self = this;
      Modal.body.querySelector('[data-action="change"]').addEventListener('click', function () {
        self.inlineError = null;
        self.setState('upload');
      });
      Modal.body.querySelector('[data-action="removephoto"]').addEventListener('click', function () {
        self.removePhoto();
      });
      Modal.body.querySelector('[data-action="generate"]').addEventListener('click', function () {
        self.startGenerate('new');
      });
      this.wireCommon();
    },

    // The shopper assumes we keep their photo; the kill switch proves we
    // don't. Held in memory for the undo window only.
    removePhoto: function () {
      var self = this;
      var old = this.photoBlob;
      this.photoBlob = null;
      Store.deletePhoto().then(function () {
        if (!Modal.isOpen) return;
        self.inlineError = null;
        self.setState('upload');
        Modal.toast('Photo removed from this device.', 'Undo', function () {
          self.photoBlob = old;
          Store.setPhoto(old).then(function () {
            if (Modal.isOpen && self.state === 'upload') self.setState('ready');
          });
        });
      });
    },

    renderGenerating: function () {
      var backdrop = this._genBackdrop || this.photoBlob;
      var photoUrl = backdrop ? this.url(backdrop) : '';
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill">' +
          '<div class="product-tryon-develop">' +
            (photoUrl
              ? '<img class="product-tryon-develop__photo" src="' + photoUrl + '" alt="">'
              : '') +
            '<div class="product-tryon-develop__veil" aria-hidden="true"></div>' +
            '<div class="product-tryon-progress">' +
              '<p class="product-tryon-progress__pct">0%</p>' +
              '<div class="product-tryon-progress__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="Generating your try-on">' +
                '<span style="width:0%"></span>' +
              '</div>' +
              '<p class="product-tryon-progress__stage" role="status">Reading your photo</p>' +
            '</div>' +
          '</div>' +
        '</div>';
    },

    // -- camera capture -------------------------------------------------------

    renderCamera: function () {
      var self = this;
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill">' +
          '<button type="button" class="product-tryon-back" data-action="back">' +
            '<span class="product-tryon-back__chevron" aria-hidden="true">' + ICONS.chevron + '</span> Back' +
          '</button>' +
          '<div class="product-tryon-camstage">' +
            '<video class="product-tryon-camstage__video" autoplay muted playsinline aria-label="Camera preview"></video>' +
            '<p class="product-tryon-count" role="status" hidden></p>' +
          '</div>' +
          '<p class="product-tryon-camhint">Step back so your whole body is in frame.</p>' +
          '<button type="button" class="product-tryon-shutter" aria-label="Take photo"></button>' +
          '<p class="product-tryon-privacy product-tryon-privacy--center">' + ICONS.lock + ' Your photo is processed once and never stored on our servers.</p>' +
        '</div>';

      var video = Modal.body.querySelector('.product-tryon-camstage__video');
      var shutter = Modal.body.querySelector('.product-tryon-shutter');

      Modal.body.querySelector('[data-action="back"]').addEventListener('click', function () {
        Camera.stop();
        self.setState('upload');
      });
      shutter.addEventListener('click', function () {
        self.startCountdown(video, shutter);
      });

      Camera.start().then(function (stream) {
        if (!Modal.isOpen || self.state !== 'camera') {
          Camera.stop();
          return;
        }
        video.srcObject = stream;
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () { /* muted autoplay */ });
        try { shutter.focus(); } catch (_) { /* ignore */ }
      }, function (err) {
        if (!Modal.isOpen || self.state !== 'camera') return;
        var name = err && err.name;
        self.inlineError = {
          message: name === 'NotAllowedError' || name === 'SecurityError'
            ? 'Camera access was blocked. You can allow it in your browser settings or upload a photo instead.'
            : "We couldn't start the camera. Upload a photo instead.",
        };
        self.setState('upload');
      });
    },

    // The shutter always counts down from 3: the person needs a beat to pose.
    startCountdown: function (video, shutter) {
      if (Camera.countTimer) return;
      var self = this;
      var count = 3;
      var el = Modal.body.querySelector('.product-tryon-count');
      if (!el) return;
      shutter.disabled = true;
      shutter.classList.add('is-armed');
      el.hidden = false;
      el.textContent = String(count);
      Camera.countTimer = setInterval(function () {
        if (!Modal.isOpen || self.state !== 'camera') {
          Camera.stop();
          return;
        }
        count--;
        if (count > 0) {
          el.textContent = String(count);
          if (!reducedMotion()) {
            el.style.animation = 'none';
            void el.offsetWidth;
            el.style.animation = '';
          }
        } else {
          clearInterval(Camera.countTimer);
          Camera.countTimer = null;
          el.hidden = true;
          self.capturePhoto(video);
        }
      }, 1000);
    },

    capturePhoto: function (video) {
      var self = this;
      var fail = function () {
        Camera.stop();
        self.inlineError = { message: "We couldn't take the photo. Try again or upload one instead." };
        self.setState('upload');
      };
      var w = video.videoWidth;
      var h = video.videoHeight;
      if (!w || !h) { fail(); return; }
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');
      if (!ctx) { fail(); return; }
      try {
        // The preview is mirrored like a mirror; the capture is the raw
        // frame so text and logos are not reversed.
        ctx.drawImage(video, 0, 0, w, h);
      } catch (_) {
        fail();
        return;
      }
      Camera.stop();
      try {
        canvas.toBlob(function (blob) {
          if (!Modal.isOpen) return;
          if (!blob) { fail(); return; }
          self.handleFile(blob, null);
        }, 'image/jpeg', 0.92);
      } catch (_) {
        fail();
      }
    },

    beginProgress: function () {
      this.endProgress();
      this.progress = 0;
      var self = this;
      this.progressTimer = setInterval(function () {
        self.progress += (90 - self.progress) * 0.035;
        if (self.progress > 90) self.progress = 90;
        self.updateProgressUI();
      }, 250);
    },

    endProgress: function () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },

    updateProgressUI: function () {
      var pct = Modal.body.querySelector('.product-tryon-progress__pct');
      if (!pct) return;
      var bar = Modal.body.querySelector('.product-tryon-progress__bar');
      var fill = bar && bar.querySelector('span');
      var stage = Modal.body.querySelector('.product-tryon-progress__stage');
      var p = Math.round(this.progress);
      pct.textContent = p + '%';
      if (fill) fill.style.width = p + '%';
      if (bar) bar.setAttribute('aria-valuenow', String(p));
      if (stage) {
        stage.textContent = p < 30 ? 'Reading your photo' : p < 70 ? 'Fitting the item' : 'Final touches';
      }
    },

    startGenerate: function (mode) {
      if (this.generating) return;
      var isLayer = mode === 'layer';
      var sourceBlob;
      if (isLayer && this.layerTarget) sourceBlob = this.layerTarget.resultBlob;
      else if (isLayer) sourceBlob = this.resultBlob;
      else sourceBlob = this.photoBlob;
      var handle = isLayer && this.pendingRec ? this.pendingRec.handle : this.product.handle;
      if (!sourceBlob || !handle) return;
      if (!isLayer) {
        this.outfit = [this.primaryItem()];
        this.pendingRec = null;
        this.layerTarget = null;
      }
      this._genBackdrop = sourceBlob;
      this.generating = true;
      this.inlineError = null;
      this.layerError = false;
      this.setState('generating');
      this.beginProgress();

      var self = this;
      Api.generate({ photoBlob: sourceBlob, productHandle: handle, mode: mode }).then(function (result) {
        self.generating = false;
        if (!Modal.isOpen) { self.endProgress(); return; }
        if (result.ok) self.onGenerateSuccess(result, mode);
        else self.onGenerateError(result, mode);
      });
    },

    onGenerateSuccess: function (result, mode) {
      var blob = base64ToBlob(result.image.base64, result.image.mimeType);
      if (!blob) {
        this.onGenerateError({ ok: false, error: 'generation_failed' }, mode);
        return;
      }
      if (typeof result.remaining === 'number') this.remaining = result.remaining;
      track('TryOn Generated');
      var self = this;
      var added = this.pendingRec;
      this.pendingRec = null;
      if (added) {
        this.recQueue = this.recQueue.filter(function (r) { return r.id !== added.id; });
      }

      // Layering onto a saved look: update the wardrobe entry in place.
      if (mode === 'layer' && this.layerTarget) {
        var entry = this.layerTarget;
        this.layerTarget = null;
        entry.resultBlob = blob;
        entry.mimeType = result.image.mimeType || 'image/jpeg';
        if (added) entry.products.push(added);
        entry.createdAt = Date.now();
        Store.updateLook(entry);
        this.endProgress();
        this.progress = 100;
        this.updateProgressUI();
        setTimeout(function () {
          if (!Modal.isOpen) return;
          self.setState('look');
          Modal.toast('Saved.');
        }, reducedMotion() ? 0 : 250);
        return;
      }

      this.resultBlob = blob;
      this.resultMime = result.image.mimeType || 'image/jpeg';
      if (mode === 'layer' && added) {
        this.outfit.push(added);
      }

      // Persist to the wardrobe (fire and forget; UI moves on immediately).
      Store.addLook({
        resultBlob: blob,
        mimeType: this.resultMime,
        products: this.outfit.slice(),
        mode: mode,
        createdAt: Date.now(),
      }).then(function () {
        return Store.countWardrobe();
      }).then(function (count) {
        self.wardrobeCount = count || self.wardrobeCount;
      });

      this.ensureRecs(this.product.id, this.outfit);

      // Snap to 100, hold a beat, then reveal.
      this.endProgress();
      this.progress = 100;
      this.updateProgressUI();
      setTimeout(function () {
        if (!Modal.isOpen) return;
        self.setState('result');
        Modal.toast('Saved to your try-ons.');
      }, reducedMotion() ? 0 : 250);
    },

    onGenerateError: function (result, mode) {
      this.endProgress();
      var wasLookLayer = !!this.layerTarget;
      this.layerTarget = null;
      this.pendingRec = null;
      if (result.aborted) return; // user closed the modal

      if (result.error === 'daily_limit') {
        this.remaining = 0;
        this.setState('limit');
        return;
      }
      if (result.error === 'disabled') {
        this.markDisabled();
        this.setState('unavailable');
        return;
      }
      if (mode === 'layer') {
        // daily_limit and disabled routed above; rate limiting keeps the
        // current screen but says when to retry.
        this.layerError = result.error === 'rate_limited'
          ? 'One moment. Try again in ' + (parseInt(result.retryAfter, 10) || 30) + 's.'
          : "Couldn't add that item. Try again.";
        this.setState(wasLookLayer ? 'look' : 'result');
        return;
      }

      var message;
      var retry = false;
      switch (result.error) {
        case 'photo_not_processable':
          message = "We couldn't process that photo. Try a different one with better lighting.";
          break;
        case 'invalid_image':
          message = result.code === 'IMAGE_TOO_SMALL'
            ? 'That photo is a bit small. Use one at least 200px on each side.'
            : "We couldn't read that photo. Try a different one.";
          break;
        case 'rate_limited':
          message = 'One moment. Try again in ' + (parseInt(result.retryAfter, 10) || 30) + 's.';
          retry = true;
          break;
        case 'file_too_large':
          message = 'That photo is over 10MB. Choose a smaller one.';
          break;
        case 'unsupported_type':
          message = 'That file format did not work. Use a JPG or PNG photo.';
          break;
        case 'product_unavailable':
          message = 'Try-on is not available for this item yet.';
          break;
        case 'invalid_visitor_id':
          Visitor.regenerate();
          message = 'Something went sideways. Try again.';
          retry = true;
          break;
        case 'network':
          // The request may have completed server-side, so no promise about
          // the credit here.
          message = 'We lost the connection. Check your try-ons in a moment or try again.';
          retry = true;
          break;
        default:
          message = 'Something went wrong on our end. Your try-on credit was not used. Try again.';
          retry = true;
      }
      this.inlineError = { message: message, retry: retry };
      this.setState('ready');
    },

    // -- result + complete the look ------------------------------------------

    outfitTitles: function (products) {
      return (products || this.outfit).map(function (p) { return p.title; }).join(' + ');
    },

    outfitTotal: function (products) {
      var sum = 0;
      var list = products || this.outfit;
      for (var i = 0; i < list.length; i++) sum += list[i].price || 0;
      return sum;
    },

    nextRec: function () {
      return this.nextRecFor(this.outfit);
    },

    // Dismissal is remembered per primary product for the session so the
    // card does not pop back on re-render or reopen.
    _ctlDismissMem: {},

    isCtlDismissed: function (id) {
      if (this._ctlDismissMem[id]) return true;
      try {
        return !!sessionStorage.getItem('sanction_tryon_ctl_dismissed_' + id);
      } catch (_) {
        return false;
      }
    },

    dismissCtl: function (id) {
      this._ctlDismissMem[id] = true;
      try {
        sessionStorage.setItem('sanction_tryon_ctl_dismissed_' + id, '1');
      } catch (_) { /* memory flag covers the session */ }
    },

    // First queued recommendation that is not already in the given outfit,
    // not the same product type as anything in it (a second beanie is a
    // substitute, not a completion), and not in a different gender/age lane
    // than the outfit's primary product. Unisex/no-signal recs always pass.
    nextRecFor: function (list) {
      var ids = {};
      var types = {};
      var primaryLane = list.length ? productLane(list[0]) : null;
      for (var i = 0; i < list.length; i++) {
        ids[list[i].id] = true;
        var t = normType(list[i].type);
        if (t) types[t] = true;
      }
      for (var j = 0; j < this.recQueue.length; j++) {
        var rec = this.recQueue[j];
        if (ids[rec.id]) continue;
        if (rec.type && types[rec.type]) continue;
        if (primaryLane) {
          var recLane = productLane(rec);
          if (recLane && recLane !== primaryLane) continue;
        }
        return rec;
      }
      return null;
    },

    // Fetch recommendations for sourceId unless the queue already belongs to
    // it. Complementary intent first (related items suggest substitutes, e.g.
    // eight more goggles for a goggle); falls back to the default request.
    ensureRecs: function (sourceId, outfitList) {
      var self = this;
      sourceId = String(sourceId);
      if (this.recSourceId === sourceId && this.recPromise) return this.recPromise;
      this.recSourceId = sourceId;
      this.recQueue = [];
      var excludeTypes = {};
      for (var i = 0; i < (outfitList || []).length; i++) {
        var t = normType(outfitList[i].type);
        if (t) excludeTypes[t] = true;
      }
      var primaryLane = outfitList && outfitList.length ? productLane(outfitList[0]) : null;
      this.recPromise = Api.recommendations(sourceId, 'complementary').then(function (products) {
        if (products && products.length) return products;
        return Api.recommendations(sourceId);
      }).then(function (products) {
        if (self.recSourceId !== sourceId) return; // superseded by a newer fetch
        self.recQueue = self.mapRecs(products, excludeTypes, primaryLane);
      });
      return this.recPromise;
    },

    mapRecs: function (products, excludeTypes, primaryLane) {
      return products.filter(function (p) {
        var type = normType(p.type);
        if (CONFIG.wearableTypes.indexOf(type) === -1) return false;
        if (excludeTypes[type]) return false;
        if (primaryLane) {
          var lane = productLane(p);
          if (lane && lane !== primaryLane) return false;
        }
        return p.variants && p.variants.length > 0;
      }).map(function (p) {
        var variant = null;
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].available) { variant = p.variants[i]; break; }
        }
        if (!variant) variant = p.variants[0];
        return {
          id: String(p.id),
          title: p.title,
          price: moneyNumber(p.price),
          variantId: String(variant.id),
          handle: p.handle,
          image: p.featured_image || '',
          type: normType(p.type),
        };
      });
    },

    // Result-screen recs: fetch (or refresh a stale queue), and when they
    // arrive after the screen has rendered, re-render and scroll the card
    // into view so it is always seen.
    ensureRecsForResult: function () {
      var self = this;
      this.ensureRecs(this.product.id, this.outfit).then(function () {
        if (!Modal.isOpen || self.state !== 'result') return;
        if (self.isCtlDismissed(self.product.id)) return;
        if (!self.nextRec()) return;
        if (!Modal.body.querySelector('.product-tryon-ctl')) {
          self.render();
          self.scrollCtlIntoView();
        }
      });
    },

    scrollCtlIntoView: function () {
      var card = Modal.body.querySelector('.product-tryon-ctl');
      if (card && typeof card.scrollIntoView === 'function') {
        try {
          card.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
        } catch (_) { /* ignore */ }
      }
    },

    completeLookHtml: function (rec, extraClass, heading) {
      if (!rec) return '';
      return (
        '<aside class="product-tryon-ctl' + (extraClass || '') + '">' +
          '<button type="button" class="product-tryon-ctl__dismiss" data-action="ctl-dismiss" aria-label="Dismiss suggestion">' + ICONS.close + '</button>' +
          '<h3 class="product-tryon-ctl__head"><span aria-hidden="true">' + ICONS.sparkle + '</span> ' + (heading || 'Complete the look') + '</h3>' +
          '<div class="product-tryon-ctl__row">' +
            (rec.image
              ? '<img class="product-tryon-ctl__img" src="' + escapeHtml(rec.image) + '" alt="" loading="lazy">'
              : '') +
            '<div class="product-tryon-ctl__meta">' +
              '<p class="product-tryon-ctl__title">' + escapeHtml(rec.title) + '</p>' +
              '<p class="product-tryon-ctl__price">' + formatMoney(rec.price) + '</p>' +
            '</div>' +
            '<button type="button" class="product-tryon-ctl__btn" data-action="layer">+ Try it on too</button>' +
          '</div>' +
        '</aside>'
      );
    },

    renderResult: function () {
      var resultUrl = this.resultBlob ? this.url(this.resultBlob) : '';
      var titles = this.outfitTitles();
      var altText = 'You wearing ' + escapeHtml(titles);
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill">' +
          (resultUrl
            ? '<div class="product-tryon-stage"><div class="product-tryon-stage__frame">' +
                '<img class="product-tryon-result__img" src="' + resultUrl + '" alt="' + altText + '">' +
                '<button type="button" class="product-tryon-expand" aria-label="Zoom image">' + ICONS.expand + '</button>' +
              '</div></div>'
            : '') +
          this.completeLookHtml(this.isCtlDismissed(this.product.id) ? null : this.nextRec()) +
          '<div class="product-tryon-outfit">' +
            '<p class="product-tryon-outfit__titles">' + escapeHtml(titles) + '</p>' +
            '<p class="product-tryon-outfit__total">' + formatMoney(this.outfitTotal()) + '</p>' +
          '</div>' +
          (this.layerError
            ? '<p class="product-tryon-layer-error" role="alert">' + escapeHtml(this.layerError) + '</p>'
            : '') +
          this.creditsHtml() +
          '<div class="product-tryon-actions">' +
            '<button type="button" class="btn product-tryon-cta" data-action="atc">Add to cart</button>' +
            '<a class="product-tryon-viewcart" href="/cart" hidden>View cart</a>' +
            '<button type="button" class="product-tryon-secondary" data-action="newphoto">Try another photo</button>' +
          '</div>' +
          this.wardrobeRowHtml() +
        '</div>';

      var self = this;
      Modal.body.querySelector('[data-action="atc"]').addEventListener('click', function () {
        self.addToCart(this);
      });
      Modal.body.querySelector('[data-action="newphoto"]').addEventListener('click', function () {
        self.inlineError = null;
        self.setState('upload');
      });
      var layerBtn = Modal.body.querySelector('[data-action="layer"]');
      if (layerBtn) {
        layerBtn.addEventListener('click', function () {
          if (self.generating) return;
          self.pendingRec = self.nextRec();
          if (self.pendingRec) self.startGenerate('layer');
        });
      }
      var dismissBtn = Modal.body.querySelector('[data-action="ctl-dismiss"]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          self.dismissCtl(self.product.id);
          self.render(); // the stage reclaims the card's space
        });
      }
      this.wireViewer(resultUrl, altText);
      this.wireCommon();
      // Covers both a stale queue (recs fetched for another look) and recs
      // that arrive after this screen rendered.
      this.ensureRecsForResult();
    },

    // Tapping the image or the frosted expand button opens the lightbox.
    wireViewer: function (url, alt) {
      if (!url) return;
      var expand = Modal.body.querySelector('.product-tryon-expand');
      var img = Modal.body.querySelector('.product-tryon-result__img');
      if (!expand || !img) return;
      var openViewer = function () { Viewer.open(url, img.alt, expand); };
      expand.addEventListener('click', openViewer);
      img.addEventListener('click', openViewer);
    },

    // -- add to cart ----------------------------------------------------------

    addToCart: function (btn, products, primaryLive) {
      if (this.atcBusy) return;
      this.atcBusy = true;
      var list = products || this.outfit;
      var useLive = primaryLive !== false && !products;
      var self = this;
      var items = [];
      for (var i = 0; i < list.length; i++) {
        var vid = i === 0 && useLive ? this.liveVariantId() : list[i].variantId;
        var idNum = parseInt(vid, 10);
        if (idNum) items.push({ id: idNum, quantity: 1 });
      }
      if (!items.length) { this.atcBusy = false; return; }

      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';

      var failed = false;
      var chain = Promise.resolve();
      items.forEach(function (item) {
        chain = chain.then(function () {
          if (failed) return;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] }),
          }).then(function (res) {
            if (!res.ok) failed = true;
          }, function () {
            failed = true;
          });
        });
      });

      chain.then(function () {
        self.atcBusy = false;
        if (failed) {
          btn.disabled = false;
          btn.textContent = original;
          Modal.toast("Couldn't add to cart. Try again.");
          return;
        }
        track('TryOn Add to Cart');
        Api.cartToken().then(function (token) {
          // productId carries the HANDLE to match server-side event keying.
          Api.event({
            eventType: 'add_to_cart',
            productId: list[0].handle || list[0].id,
            productTitle: list[0].title,
            variantId: String(items[0].id),
            cartToken: token,
            visitorId: Visitor.id(),
          });
        });
        btn.textContent = 'Added ✓';
        if (document.querySelector('cart-drawer')) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          Modal.close();
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        } else {
          var link = Modal.body.querySelector('.product-tryon-viewcart');
          if (link) link.hidden = false;
        }
      });
    },

    // -- wardrobe -------------------------------------------------------------

    renderWardrobe: function () {
      var self = this;
      Modal.body.innerHTML =
        '<div class="product-tryon-screen">' +
          '<button type="button" class="product-tryon-back" data-action="back">' +
            '<span class="product-tryon-back__chevron" aria-hidden="true">' + ICONS.chevron + '</span> Back' +
          '</button>' +
          '<h2 class="product-tryon-headline">My try-ons</h2>' +
          '<div class="product-tryon-wgrid" aria-busy="true"></div>' +
        '</div>';
      Modal.body.querySelector('[data-action="back"]').addEventListener('click', function () {
        self.setState(self.returnState || 'ready');
      });

      Store.listWardrobe().then(function (entries) {
        if (!Modal.isOpen || self.state !== 'wardrobe') return;
        self.wardrobeCount = entries.length;
        var grid = Modal.body.querySelector('.product-tryon-wgrid');
        if (!grid) return;
        grid.setAttribute('aria-busy', 'false');
        if (!entries.length) {
          grid.outerHTML = '<p class="product-tryon-wempty">Nothing saved yet. Your try-ons land here.</p>';
          return;
        }
        var html = '';
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          var titles = self.outfitTitles(e.products);
          var wUrl = self.url(e.resultBlob);
          html +=
            '<div class="product-tryon-wcard" data-id="' + e.id + '">' +
              '<button type="button" class="product-tryon-wcard__view" data-waction="view" aria-label="View this try-on: ' + escapeHtml(titles) + '">' +
                (wUrl
                  ? '<img class="product-tryon-wcard__img" src="' + wUrl + '" alt="">'
                  : '') +
                '<span class="product-tryon-wcard__meta">' +
                  '<span class="product-tryon-wcard__title">' + escapeHtml(titles) + '</span>' +
                  '<span class="product-tryon-wcard__price">' + formatMoney(self.outfitTotal(e.products)) + '</span>' +
                '</span>' +
              '</button>' +
              // Delete floats over the photo corner (frosted circle, same
              // family as the gallery pill) so Add to cart owns the full
              // action row and never wraps. After the view button in the DOM
              // so tab order goes view, then remove.
              '<button type="button" class="product-tryon-wcard__del" data-waction="del" aria-label="Remove this try-on">' + ICONS.trash + '</button>' +
              '<div class="product-tryon-wcard__actions">' +
                '<button type="button" class="product-tryon-wcard__atc" data-waction="atc">Add to cart</button>' +
              '</div>' +
            '</div>';
        }
        grid.innerHTML = html;
        grid.addEventListener('click', function (ev) {
          var actionBtn = ev.target.closest('[data-waction]');
          if (!actionBtn) return;
          var card = actionBtn.closest('.product-tryon-wcard');
          var id = Number(card.getAttribute('data-id'));
          var entry = null;
          for (var k = 0; k < entries.length; k++) {
            if (Number(entries[k].id) === id) { entry = entries[k]; break; }
          }
          if (!entry) return;
          var action = actionBtn.getAttribute('data-waction');
          if (action === 'atc') {
            self.addToCart(actionBtn, entry.products, false);
          } else if (action === 'view') {
            self.openLook(entry);
          } else {
            self.deleteLook(entry);
          }
        });
        grid.insertAdjacentHTML(
          'afterend',
          '<button type="button" class="product-tryon-quietlink product-tryon-quietlink--clear" data-action="clearall">Clear all</button>'
        );
        Modal.body.querySelector('[data-action="clearall"]').addEventListener('click', function () {
          self.clearAllLooks(entries);
        });
      });
    },

    // Wipes every saved look; the entries stay in memory for the 5s undo
    // window only.
    clearAllLooks: function (entries) {
      var self = this;
      Store.clearWardrobe().then(function () {
        self.wardrobeCount = 0;
        if (Modal.isOpen && self.state === 'wardrobe') self.renderWardrobe();
        Modal.toast('Try-ons removed from this device.', 'Undo', function () {
          var chain = Promise.resolve();
          entries.forEach(function (e) {
            chain = chain.then(function () { return Store.putLook(e); });
          });
          chain.then(function () {
            self.wardrobeCount = entries.length;
            if (Modal.isOpen && self.state === 'wardrobe') self.renderWardrobe();
          });
        });
      });
    },

    // -- look detail view -----------------------------------------------------

    openLook: function (entry) {
      this.currentLook = entry;
      this.layerError = false;
      this.setState('look');
    },

    renderLook: function () {
      var entry = this.currentLook;
      if (!entry || !entry.products || !entry.products.length) {
        this.setState('wardrobe');
        return;
      }
      var self = this;
      var imgUrl = entry.resultBlob ? this.url(entry.resultBlob) : '';
      var titles = this.outfitTitles(entry.products);
      var sourceId = String(entry.products[0].id);
      var rec = this.recSourceId === sourceId && !this.isCtlDismissed(sourceId)
        ? this.nextRecFor(entry.products)
        : null;
      var lookAlt = 'Saved try-on: ' + escapeHtml(titles);

      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill">' +
          '<button type="button" class="product-tryon-back" data-action="back">' +
            '<span class="product-tryon-back__chevron" aria-hidden="true">' + ICONS.chevron + '</span> Back' +
          '</button>' +
          (imgUrl
            ? '<div class="product-tryon-stage"><div class="product-tryon-stage__frame">' +
                '<img class="product-tryon-result__img" src="' + imgUrl + '" alt="' + lookAlt + '">' +
                '<button type="button" class="product-tryon-expand" aria-label="Zoom image">' + ICONS.expand + '</button>' +
              '</div></div>'
            : '') +
          this.completeLookHtml(rec, '', 'Add an item') +
          '<div class="product-tryon-outfit">' +
            '<p class="product-tryon-outfit__titles">' + escapeHtml(titles) + '</p>' +
            '<p class="product-tryon-outfit__total">' + formatMoney(this.outfitTotal(entry.products)) + '</p>' +
          '</div>' +
          (this.layerError
            ? '<p class="product-tryon-layer-error" role="alert">' + escapeHtml(this.layerError) + '</p>'
            : '') +
          this.creditsHtml() +
          '<div class="product-tryon-actions">' +
            '<button type="button" class="btn product-tryon-cta" data-action="atc">Add to cart</button>' +
            '<a class="product-tryon-viewcart" href="/cart" hidden>View cart</a>' +
            '<button type="button" class="product-tryon-secondary" data-action="delete">Delete this look</button>' +
          '</div>' +
        '</div>';

      Modal.body.querySelector('[data-action="back"]').addEventListener('click', function () {
        self.currentLook = null;
        self.setState('wardrobe');
      });
      Modal.body.querySelector('[data-action="atc"]').addEventListener('click', function () {
        self.addToCart(this, entry.products, false);
      });
      Modal.body.querySelector('[data-action="delete"]').addEventListener('click', function () {
        self.deleteLook(entry);
      });
      var layerBtn = Modal.body.querySelector('[data-action="layer"]');
      if (layerBtn) {
        layerBtn.addEventListener('click', function () {
          if (self.generating) return;
          var r = self.nextRecFor(entry.products);
          if (!r) return;
          self.layerTarget = entry;
          self.pendingRec = r;
          self.startGenerate('layer');
        });
      }
      var dismissBtn = Modal.body.querySelector('[data-action="ctl-dismiss"]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          self.dismissCtl(sourceId);
          self.renderLook();
        });
      }
      this.wireViewer(imgUrl, lookAlt);

      // Fetch recs for this look's primary product if the queue belongs to
      // another product; re-render with the card once they arrive.
      this.ensureRecs(sourceId, entry.products).then(function () {
        if (!Modal.isOpen || self.state !== 'look' || self.currentLook !== entry) return;
        if (self.isCtlDismissed(sourceId)) return;
        if (!self.nextRecFor(entry.products)) return;
        if (!Modal.body.querySelector('.product-tryon-ctl')) {
          self.renderLook();
          self.scrollCtlIntoView();
        }
      });
    },

    deleteLook: function (entry) {
      var self = this;
      Store.deleteLook(entry.id).then(function () {
        self.wardrobeCount = Math.max(0, self.wardrobeCount - 1);
        if (Modal.isOpen && (self.state === 'wardrobe' || self.state === 'look')) {
          self.currentLook = null;
          self.setState('wardrobe');
        }
        Modal.toast('Removed.', 'Undo', function () {
          Store.putLook(entry).then(function () {
            self.wardrobeCount += 1;
            if (Modal.isOpen && self.state === 'wardrobe') self.renderWardrobe();
          });
        });
      });
    },

    // -- limit / unavailable --------------------------------------------------

    renderLimit: function () {
      var limitKnown = typeof this.dailyLimit === 'number' && this.dailyLimit > 0;
      var headline = !limitKnown || this.dailyLimit === 5 ? "That's today's five" : "That's all for today";
      var more = !limitKnown || this.dailyLimit === 5 ? 'five more' : this.dailyLimit + ' more';
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill product-tryon-screen--vcenter product-tryon-center">' +
          '<p class="product-tryon-bigmark" aria-hidden="true">' + ICONS.sparkle + '</p>' +
          '<h2 class="product-tryon-headline">' + headline + '</h2>' +
          '<p class="product-tryon-sub">You have used today\'s try-ons. Come back tomorrow for ' + more + '.</p>' +
          this.wardrobeRowHtml() +
        '</div>';
      this.wireCommon();
    },

    renderUnavailable: function () {
      Modal.body.innerHTML =
        '<div class="product-tryon-screen product-tryon-screen--fill product-tryon-screen--vcenter product-tryon-center">' +
          '<h2 class="product-tryon-headline">Try-on is taking a break.</h2>' +
          '<p class="product-tryon-sub">Check back soon.</p>' +
        '</div>';
    },
  };

  // ---------------------------------------------------------------------------
  // Init — reveal the entry button; zero network until first click.
  // ---------------------------------------------------------------------------
  function init() {
    App.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
